import React, { useState } from 'react'
import { Button, Col, Form, Row } from 'react-bootstrap'
import { ErrorMessage, Field, Form as FormikForm, Formik } from 'formik'
import { FaRegEye, FaRegEyeSlash } from 'react-icons/fa'
import { LoginReq } from '../types/models/auth'
import { setCredentials } from '../store/slices/authSlice'
import { useAppDispatch } from '../store/hooks'
import { useNavigate } from 'react-router-dom'
import { useLoginMutation } from '../services/authApi'
import { LoginResponse } from '../types/api/responses'
import ParticleMorpherCanvas from '../constants/ParticleMorpher'
import { activeBranding as branding } from '../config'
import { useGetOrganizationPublicQuery } from '../services/orgsApi'
import { useOrganizationBranding } from '../hooks/useOrganizationBranding'
import { FetchBaseQueryError } from '@reduxjs/toolkit/query'

// Assuming you have a selector for login status
import * as Yup from 'yup'
import ErrorDialog from '../components/modals/ErrorDialog'

// Validation schema
const validationSchema = Yup.object().shape({
  username: Yup.string().required('Username is required'),
  password: Yup.string().required('Password is required'),
})

const Login: React.FC = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [showError, setShowError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const [login] = useLoginMutation()

  // Organization branding setup - using public endpoints
  const { data: orgData } = useGetOrganizationPublicQuery()
  const { logoUrl: organizationLogoUrl } = useOrganizationBranding()

  // State for the logo URL
  const [logoUrl, setLogoUrl] = useState<string>(branding.assets.logoSmall)

  // Update logo URL when organization branding changes
  React.useEffect(() => {
    if (organizationLogoUrl) {
      setLogoUrl(organizationLogoUrl)
    } else {
      setLogoUrl(branding.assets.logoSmall)
    }
  }, [organizationLogoUrl])

  // Autofocus and select handled via input props on the Field

  const isFetchBaseQueryError = (error: unknown): error is FetchBaseQueryError => {
    return typeof error === 'object' && error !== null && 'status' in error
  }

  const handleSubmit = async (credentials: LoginReq) => {
    try {
      const userData: LoginResponse = await login(credentials).unwrap()

      dispatch(setCredentials(userData))
      setTimeout(() => {
        navigate('/projects')
      }, 75)
    } catch (err: unknown) {
      setShowError(true)
      let message = 'Unknown Error'
      if (isFetchBaseQueryError(err)) {
        const data = err.data as { message?: unknown } | undefined
        if (data && typeof data.message === 'string') {
          message = data.message
        } else {
          message = `Request failed with status ${String(err.status)}`
        }
      } else if (err instanceof Error && err.message) {
        message = err.message
      }
      setErrorMessage('Failed to login: ' + message)
      navigate('/login')
    }
  }

  const [showPassword, setShowPassword] = useState(false)

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  return (
    <>
      <Row className="p-5" style={{ height: '10vh' }}>
        <Col className="d-flex align-items-center justify-content-center">
          {branding.assets.logoLarge ? (
            <>
              <img
                src={logoUrl}
                alt={`${branding.company.name} Logo`}
                style={{
                  paddingRight: '32px',
                  height: '100px',
                  aspectRatio: 'auto',
                }}
              />
              <div
                style={{
                  fontSize: '5rem',
                  fontWeight: 'bold',
                  color: '#007bff',
                  letterSpacing: '2px',
                  fontFamily: 'Arial, sans-serif',
                  textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                }}
              >
                {orgData?.name || branding.company.name}
              </div>
            </>
          ) : (
            <img
              src={logoUrl}
              alt={`${branding.company.name} Logo`}
              style={{ height: '100px', aspectRatio: 'auto' }}
            />
          )}
        </Col>
      </Row>
      <Row>
        <Col md={6} className="p-0">
          <div
            style={{
              width: '100%',
              height: 'calc(100vh - 10vh)',
              position: 'relative',
            }}
          >
            <ParticleMorpherCanvas />
          </div>
        </Col>
        <Col
          md={6}
          className="d-flex align-items-center justify-content-center"
          style={{ height: 'calc(100vh - 10vh)' }}
        >
          <div
            className="card shadow"
            style={{
              width: '100%',
              maxWidth: '400px',
              margin: '-100px 2rem 0',
              background: branding.theme.colors.cardBackground,
              color: branding.theme.colors.text,
            }}
          >
            <div className="card-body p-4">
              <h1 className="text-white">Sign In</h1>
              <p style={{ color: branding.theme.colors.textLight }}>
                Enter your credentials to sign in and proceed further.
              </p>
              <Formik
                initialValues={{ username: '', password: '' }}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
                {({ touched, errors }) => (
                  <FormikForm>
                    <Form.Group controlId="username">
                      <Field
                        name="username"
                        type="text"
                        placeholder="Username"
                        autoFocus
                        onFocus={(e: React.FocusEvent<HTMLInputElement>) =>
                          e.currentTarget.select()
                        }
                        className={`form-control bg-dark text-white border-secondary ${
                          touched.username && errors.username ? 'is-invalid' : ''
                        }`}
                      />
                      <ErrorMessage component="div" name="username" className="invalid-feedback" />
                    </Form.Group>

                    <Form.Group controlId="password" className="password-field mt-3">
                      <div className="password-input-container">
                        <Field
                          name="password"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Password"
                          className={`form-control bg-dark text-white border-secondary password-input ${
                            touched.password && errors.password ? 'is-invalid' : ''
                          }`}
                        />
                        {showPassword ? (
                          <FaRegEyeSlash
                            className="toggle-password-icon"
                            onClick={togglePasswordVisibility}
                            color={branding.theme.colors.primaryLight}
                          />
                        ) : (
                          <FaRegEye
                            className="toggle-password-icon"
                            onClick={togglePasswordVisibility}
                            color={branding.theme.colors.primary}
                          />
                        )}
                      </div>
                      <ErrorMessage component="div" name="password" className="invalid-feedback" />
                    </Form.Group>
                    <Button variant="primary" type="submit" className="sign-in-btn w-100 mt-4">
                      Sign In
                    </Button>
                    <p className="terms-text mt-3 small text-center">
                      <span style={{ color: branding.theme.colors.textLight }}>
                        By signing in, you agree to our
                      </span>
                      <br />
                      <a href="/termsConditions" style={{ color: branding.theme.colors.primary }}>
                        Terms of Use
                      </a>{' '}
                      &{' '}
                      <a href="/privacyPolicy" style={{ color: branding.theme.colors.primary }}>
                        Privacy Policy
                      </a>
                    </p>
                  </FormikForm>
                )}
              </Formik>
            </div>
          </div>
        </Col>
      </Row>
      <ErrorDialog
        show={showError}
        onHide={() => setShowError(false)}
        message={errorMessage}
        title="Login Error"
      />
    </>
  )
}

export default Login
